import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import { Truck, Clock, MapPin, Package, Phone, Mail } from "lucide-react";

const ShippingPolicy = () => {
  return (
    <Layout>
      <Helmet>
        <title>Shipping Policy - Fast & Secure Delivery | TrueCore Gadgets India</title>
        <meta name="description" content="TrueCore Gadgets shipping policy - Fast delivery across India in 3-7 days. Free shipping on orders above ₹1,999. Track your order with reliable courier partners." />
        <meta name="keywords" content="TrueCore shipping policy, delivery India, free shipping, order tracking, courier services, fast delivery gadgets" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://truecoregadgets.com/shipping-policy" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Shipping Policy - Fast & Secure Delivery | TrueCore Gadgets" />
        <meta property="og:description" content="Fast delivery across India in 3-7 days. Free shipping on orders above ₹1,999. Track your order with reliable courier partners." />
        <meta property="og:url" content="https://truecoregadgets.com/shipping-policy" />
        <meta property="og:image" content="/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Shipping Policy - TrueCore Gadgets" />
        <meta name="twitter:description" content="Fast & secure delivery across India. Free shipping on orders above ₹1,999." />
        <meta name="twitter:image" content="/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
      </Helmet>

      <div className="container mx-auto py-12 px-4 max-w-4xl">
        <h1 className="text-4xl font-bold mb-6 gradient-text text-center">Shipping Policy</h1>
        <p className="text-lg text-center text-muted-foreground mb-12">
          At TrueCore Gadgets, we are committed to delivering your products quickly, safely, and efficiently.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-card p-6 rounded-lg shadow-md border">
            <div className="flex items-center mb-4">
              <Clock className="h-8 w-8 text-primary mr-4" />
              <h2 className="text-xl font-semibold">Order Processing Time</h2>
            </div>
            <ul className="space-y-2 text-muted-foreground">
              <li>• All orders processed within 24-48 hours</li>
              <li>• Excludes weekends and public holidays</li>
              <li>• Order confirmation email sent after checkout</li>
            </ul>
          </div>

          <div className="bg-card p-6 rounded-lg shadow-md border">
            <div className="flex items-center mb-4">
              <Truck className="h-8 w-8 text-primary mr-4" />
              <h2 className="text-xl font-semibold">Delivery Timeline</h2>
            </div>
            <ul className="space-y-2 text-muted-foreground">
              <li>• <strong>Standard Delivery:</strong> 3-7 business days</li>
              <li>• Varies by location and courier availability</li>
              <li>• Remote areas may take longer</li>
            </ul>
          </div>

          <div className="bg-card p-6 rounded-lg shadow-md border">
            <div className="flex items-center mb-4">
              <MapPin className="h-8 w-8 text-primary mr-4" />
              <h2 className="text-xl font-semibold">Delivery Coverage</h2>
            </div>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Ships across all serviceable pin codes in India</li>
              <li>• Remote locations may have restrictions</li>
              <li>• Coverage verification at checkout</li>
            </ul>
          </div>

          <div className="bg-card p-6 rounded-lg shadow-md border">
            <div className="flex items-center mb-4">
              <Package className="h-8 w-8 text-primary mr-4" />
              <h2 className="text-xl font-semibold">Shipping Charges</h2>
            </div>
            <ul className="space-y-2 text-muted-foreground">
              <li>• <strong>Free shipping</strong> on orders above ₹1,999</li>
              <li>• Flat rate ₹99 for orders below ₹1,999</li>
              <li>• No hidden charges</li>
            </ul>
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-8 mb-12">
          <h2 className="text-2xl font-bold mb-6">🔍 Tracking Your Order</h2>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Once your order is shipped, you will receive a tracking number via email or SMS.
            </p>
            <p className="text-muted-foreground">
              Track your shipment using the courier partner's official website or via our Order Tracking page.
            </p>
          </div>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">📦 Our Delivery Partners</h2>
          <p className="text-muted-foreground mb-4">We work with trusted and reliable courier services including:</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card p-4 rounded-lg text-center border">
              <strong>Delhivery</strong>
            </div>
            <div className="bg-card p-4 rounded-lg text-center border">
              <strong>Blue Dart</strong>
            </div>
            <div className="bg-card p-4 rounded-lg text-center border">
              <strong>DTDC</strong>
            </div>
            <div className="bg-card p-4 rounded-lg text-center border">
              <strong>Ekart Logistics</strong>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-card p-6 rounded-lg shadow-md border">
            <h3 className="text-xl font-semibold mb-4">❌ Delayed or Lost Shipments</h3>
            <p className="text-muted-foreground mb-4">
              If your order is delayed or appears lost, please contact us within 7 days of your expected delivery date.
            </p>
            <p className="text-muted-foreground">
              We will coordinate with our logistics partners and resolve the issue as soon as possible.
            </p>
          </div>

          <div className="bg-card p-6 rounded-lg shadow-md border">
            <h3 className="text-xl font-semibold mb-4">🔁 Return-to-Origin (RTO)</h3>
            <p className="text-muted-foreground mb-4">
              If a package is returned due to incorrect shipping information or customer unavailability, we will reship it once after reconfirmation.
            </p>
            <p className="text-muted-foreground">
              Additional shipping fees may apply for reshipment.
            </p>
          </div>
        </div>

        <div className="bg-primary/10 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-6">📞 Need Help?</h2>
          <p className="text-muted-foreground mb-6">
            If you have any questions regarding shipping, feel free to reach out to our support team:
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
            <div className="flex items-center space-x-3">
              <Mail className="text-primary" size={20} />
              <span className="font-medium">support@truecoregadgets.com</span>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="text-primary" size={20} />
              <span className="font-medium">022 6964 7168</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ShippingPolicy;
